import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import api from 'api';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Option } from 'models/Global';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import SearchIcon from 'icons/Search.icon';
import { Input } from 'ncoded-component-library';
import useDebounceSearch from 'hooks/useDebounceSearch';
import useMatchMedia from 'hooks/useMatchMedia';

import './BrokerFilter.styles.scss';

const highlightMatch = (text: string, search: string) => {
  if (!search) return text;

  return text
    .split(new RegExp(`(${search})`, 'gi'))
    .map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <b key={index}>{part}</b>
      ) : (
        part
      ),
    );
};

export type ViewAsFilterValue = { id: string; name: string };

type BrokerFilterProps = {
  className?: string;
  viewAsUserId: string;
  viewAsUserName: string;
  onSelect: (value: ViewAsFilterValue) => void;
};

const BrokerFilter: React.FC<BrokerFilterProps> = (props) => {
  const { className, onSelect, viewAsUserId, viewAsUserName } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [brokers, setBrokers] = useState<Option[]>([]);

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const classes = classNames('admin-broker-filter', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const onSearch = useCallback(async (searchString: string) => {
    if (!searchString) {
      setBrokers([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await api.portfolio.admin.getBrokers({
        $searchString: searchString,
      });

      setBrokers(
        data.map(({ userId, name }) => ({
          value: userId,
          label: name,
        })),
      );
    } catch {
      setBrokers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const {
    searchString,
    searchInputValue,
    onSearchInputChange,
    resetSearchString,
  } = useDebounceSearch({ onSearch });

  const handleOnFilterClick = useCallback(
    (id: string, name: string) => {
      onSelect({ id, name });

      resetSearchString();
      dropdown?.setIsOpen(false);
    },
    [dropdown, onSelect, resetSearchString],
  );

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      renderAsPortal={!isPhablet}
      portalClassName="admin-broker-filter__portal"
      trigerTitle={t(viewAsUserName ? 'viewAs' : 'selectAnOption', {
        name: viewAsUserName,
      })}
    >
      <Input
        prefixNode={<SearchIcon />}
        placeholder={t('search')}
        value={searchInputValue}
        onChange={({ target: { value } }) => onSearchInputChange(value)}
      />

      <ul>
        {isLoading ? (
          <li>{t('isLoading')}</li>
        ) : brokers?.length ? (
          brokers.map(({ value, label }) => (
            <li
              key={value}
              className={classNames({ selected: value === viewAsUserId })}
              onClick={() => handleOnFilterClick(value, label)}
            >
              {highlightMatch(label, searchString)}
            </li>
          ))
        ) : searchString ? (
          <li>
            {t('noDataFor')} {searchString}
          </li>
        ) : (
          <li>{t('typeToViewAvailableOptions')}</li>
        )}
      </ul>
    </FilterActionWrapper>
  );
};

export default BrokerFilter;
